import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { useCargoCountStore } from '@/store/cargoCountStore';
import { Button } from '@/components/ui';
import { CargoCountChartBar } from '@/components/cargoCount/CargoCountChartBar';
import { CARGO_COUNT_COLORS } from '@/types/cargoCount.types';
import { DragHandle, type DragHandleProps } from './DraggableWidget';

export function CargoCountWidget({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) {
  const { data, loading, fetchCargoCount } = useCargoCountStore();
  const [hasRequestedInitialData, setHasRequestedInitialData] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    setHasRequestedInitialData(true);
    fetchCargoCount();
  }, [fetchCargoCount]);

  const showLoadingState = loading || (!hasRequestedInitialData && data.length === 0);

  // Get unique area groups and select the first one
  const areas = useMemo(() => {
    return [...new Set(data.map((item) => item.area))];
  }, [data]);

  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  // Auto-select first area when data loads
  useEffect(() => {
    if (areas.length > 0 && !selectedArea) {
      setSelectedArea(areas[0]);
    }
  }, [areas, selectedArea]);

  // Filter data to selected area
  const areaData = useMemo(() => {
    if (!selectedArea) return [];
    return data.filter((item) => item.area === selectedArea);
  }, [data, selectedArea]);

  // Calculate max value for chart scaling (round to nearest 5, with padding)
  const maxValue = useMemo(() => {
    if (areaData.length === 0) return 100;

    const highest = Math.max(
      ...areaData.map((item) => Math.max(item.average, item.fixed, item.expected))
    );

    let maxRoundedOff = 5 * Math.round(highest / 5);
    if (maxRoundedOff < highest) {
      maxRoundedOff += 5;
    }
    return maxRoundedOff;
  }, [areaData]);

  // X-axis scale points (0, 20%, 40%, 60%, 80%, 100%)
  const scalePoints = [0, 0.2, 0.4, 0.6, 0.8, 1.0].map((fraction) => maxValue * fraction);

  const handleRefresh = () => {
    fetchCargoCount();
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="-mx-px -mt-px mb-4 flex items-center justify-between rounded-t-lg bg-gray-200/60 px-5 py-3 dark:bg-dark-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
            Cargo Count VLCC
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {selectedArea ? `${selectedArea} Area` : 'Loading...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dragHandleProps && <DragHandle {...dragHandleProps} />}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Link to="/cargo-count">
            <Button variant="ghost" size="sm" title="View all Cargo Count">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {showLoadingState && areaData.length === 0 ? (
        // Loading skeleton
        <div className="space-y-3 px-5 pb-5">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
              <div className="h-6 flex-1 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
            </div>
          ))}
        </div>
      ) : areaData.length > 0 ? (
        <div className="px-5 pb-5">
          {/* Legend */}
          <div className="mb-4 flex justify-center gap-4">
            <div className="flex items-center">
              <span
                className="mr-1 inline-block h-4 w-4 rounded-full"
                style={{ backgroundColor: CARGO_COUNT_COLORS.fixed }}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">Fixed</span>
            </div>
            <div className="flex items-center">
              <span
                className="mr-1 inline-block h-4 w-4 rounded-full"
                style={{ backgroundColor: CARGO_COUNT_COLORS.expected }}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">Expected</span>
            </div>
            <div className="flex items-center">
              <span
                className="mr-1 inline-block h-4 w-4 rounded-full border border-dashed"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: CARGO_COUNT_COLORS.average,
                }}
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">Average</span>
            </div>
          </div>

          {/* Chart Bars */}
          <div className="mb-3">
            {areaData.map((item, index) => (
              <CargoCountChartBar
                key={`${item.area}-${item.period}`}
                cargoCount={item}
                max={maxValue}
                last={index === areaData.length - 1}
              />
            ))}
          </div>

          {/* Scale Footer */}
          <div className="flex">
            <div className="w-[82px] min-w-[82px] flex-shrink-0" />
            <div className="flex flex-1 justify-between border-t border-gray-300 pt-1 text-xs text-gray-700 dark:border-gray-600 dark:text-gray-300">
              {scalePoints.map((point, index) => (
                <div key={index} className="relative font-medium opacity-80">
                  {Math.round(point)}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
          No cargo count data available
        </div>
      )}
    </div>
  );
}
